import React, { useEffect, useState, useMemo } from 'react';
import {
  Card,
  Typography,
  Popover,
  List,
  ListItem,
  ListItemText,
  Radio,
  Button,
} from '@mui/material';
import { useStyles } from '../UrbanSportsConnect/ConnectUrbanSports.styles';
import { useRequestIntegrations } from '~/requests/userService/useRequestIntegrations';
import { Trans, useTranslation } from 'react-i18next';
import { ToastVariant, useProvider, useToastMessage } from '~/Hooks';
import { useRequestUpdateIntRoles } from '~/requests/userService/useRequestUpdateIntRoles';
import { Error, ConfirmationModal, SearchTextField } from '~/Components';
import { useUSCConnectContext } from '../UrbanSportsConnect/UrbanSportsConnectContext';
import { useRequestActiveIntegrations } from '~/requests/userService/useRequestIntegrations/useRequestActiveIntegrations';
import { useRequestDeleteIntegration } from '~/requests/userService/useRequestDeleteIntegration';
import { useRequestIntegrationDates } from '~/requests/userService/useRequestIntegrationDates';
import { useRequestUpdateIntegrationDates } from '~/requests/userService/useRequestUpdateIntegrationDates';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { IdListCardContent } from './IdListCardContent';
import { debounce } from 'lodash-es';

export const IdListCard = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { showToast } = useToastMessage();

  const {
    error,
    setError,
    saveClicked,
    setSaveClicked,
    dates,
    setDates,
    selectedItem,
    selectedClient,
    setSelectedItem,
    setSelectedClient,
    anchorEl,
    setAnchorEl,
    showRemovalModal,
    setShowRemovalModal,
  } = useUSCConnectContext();

  const { currentProvider } = useProvider();
  const providerUUID = String(currentProvider?.uuid);

  const { startDate, endDate, initialStartDate } = dates;

  const [searchQuery, setSearchQuery] = useState('');

  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        setSearchQuery(query);
      }, 500),
    [],
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const { data: integrations } = useRequestIntegrations({
    prodOnly: 'false',
  });

  const { data: ActiveIntegrations } = useRequestActiveIntegrations({
    prodOnly: 'false',
    providerId: providerUUID,
  });

  const ActiveIntegration = ActiveIntegrations && ActiveIntegrations[0];

  const { data: integrationDates, error: getDatesError } = useRequestIntegrationDates({
    providerId: providerUUID,
    clientId: ActiveIntegration?.clientId as string,
  });

  const { data, error: roleUpdateError, mutate } = useRequestUpdateIntRoles();

  const {
    isError: deleteError,
    isSuccess: integrationDeleted,
    mutate: deleteIntegration,
  } = useRequestDeleteIntegration();

  const {
    data: updatedDates,
    mutate: updateDates,
    error: updateDatesError,
  } = useRequestUpdateIntegrationDates(providerUUID);

  const handleClosePopover = () => {
    setAnchorEl(null);
    setSearchQuery('');
  };

  const handleSelectItem = (item: string, clientName: string) => {
    setSelectedItem(item);
    setSelectedClient(clientName);
    handleClosePopover();
  };

  const handleCloseModal = () => {
    setShowRemovalModal(false);
  };

  const saveIntegration = () => {
    if (saveClicked) {
      if (!startDate && !initialStartDate) {
        showToast('USCCONNECT', t('uscconnect.no.start.date'), ToastVariant.ERROR, true);
      } else {
        if (initialStartDate && initialStartDate !== null) {
          updateDates({
            clientId: ActiveIntegration?.clientId as string,
            startDate: initialStartDate,
            endDate,
            name: ActiveIntegration?.clientName as string,
          });
          showToast('CONNECTED', t('app.integration.updated'), ToastVariant.SUCCESS, true);
        } else {
          mutate({
            providerId: providerUUID,
            userId:
              ActiveIntegration && ActiveIntegration?.clientId.length !== 0
                ? ActiveIntegration?.clientId
                : selectedItem,
            name: 'API_Access',
          });
          updateDates({
            clientId:
              ActiveIntegration && ActiveIntegration?.clientId.length !== 0
                ? ActiveIntegration?.clientId
                : selectedItem,
            startDate,
            endDate,
            name:
              ActiveIntegration && ActiveIntegration?.clientName.length !== 0
                ? ActiveIntegration?.clientName
                : selectedClient,
          });
          showToast('CONNECTED', t('app.integration.updated'), ToastVariant.SUCCESS, true);
        }
      }
    }
  };

  const removeIntegration = () => {
    if (deleteError) {
      showToast('USCCONNECT', t('uscconnect.remove.error'), ToastVariant.ERROR, true);
    }
    if (ActiveIntegration) {
      deleteIntegration({
        providerId: providerUUID,
        clientId: ActiveIntegrations[0].clientId,
      });
    }
  };

  useEffect(() => {
    saveIntegration();
    if (integrationDates?.startDate !== null) {
      setDates((prev) => ({
        ...prev,
        initialStartDate: integrationDates?.startDate ?? '',
        initialEndDate: integrationDates?.endDate ?? '',
      }));
    }

    if (roleUpdateError || startDate === '') {
      setError(roleUpdateError);
      setSaveClicked(false);
    }
    if (!roleUpdateError && (data?.providerId || updatedDates?.providerId)) {
      window.location.reload();
    }
    if (integrationDeleted) {
      setShowRemovalModal(false);
      window.location.reload();
    }
  }, [
    saveClicked,
    roleUpdateError,
    data?.providerId,
    integrationDates,
    integrationDeleted,
    updatedDates,
  ]);

  const filteredIntegrations =
    integrations?.filter((integration) =>
      integration.clientName.toLowerCase().includes(searchQuery.toLowerCase()),
    ) || [];

  return (
    <Card className={classes.card}>
      {showRemovalModal && (
        <ConfirmationModal
          open
          title={`${t('uscconnect.remove.heading')} ${ActiveIntegration?.clientName}?`}
          cancel={{ action: handleCloseModal }}
          confirm={{ action: removeIntegration, title: t('app.confirmationModal.remove') }}
          destructive
        >
          <Typography className={classes.modalParagraph} variant="body1">
            <Trans
              i18nKey="uscconnect.remove.message"
              values={{ integration: ActiveIntegration?.clientName }}
              components={{ b: <b /> }}
            />
          </Typography>
          <Typography className={classes.confirmationParagraph} variant="body1">
            <WarningAmberIcon />
            {t('app.confirmation.message')}
          </Typography>
        </ConfirmationModal>
      )}
      <Error error={error} />
      <Error error={getDatesError} />
      <Error error={updateDatesError} />

      <IdListCardContent ActiveIntegrations={ActiveIntegrations} />

      {ActiveIntegrations && ActiveIntegrations[0]?.clientId ? (
        ''
      ) : (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClosePopover}
          classes={{ paper: classes.popover }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          PaperProps={{
            style: {
              marginLeft: '9px',
              marginTop: '8px',
            },
          }}
        >
          <div>
            <SearchTextField
              id="searchIntegrations"
              onChange={(e) => debouncedSearch(e.target.value)}
              placeholder={t('app.search.integrations')}
              withBackground
              autoFocus={Boolean(anchorEl)}
            />
          </div>
          <List>
            {filteredIntegrations?.map((integration, id) => (
              <ListItem
                key={id}
                onClick={() => handleSelectItem(integration.clientId, integration.clientName)}
                className={classes.popOverListItem}
              >
                <ListItemText primary={integration.clientName} />
                <Radio checked={selectedItem === integration.clientId} className={classes.radio} />
              </ListItem>
            ))}
          </List>
          <Button onClick={handleClosePopover}>{t('app.checkin.close')}</Button>
        </Popover>
      )}
    </Card>
  );
};
